import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { amendmentQueryKeys, PAYRUN_QUERY_KEYS } from '@xemplo/gp-constants';
import {
  AmendmentChangeLog,
  AmendmentCreate,
  AmendmentFile,
  ChangeSubTask,
  ChangeTask,
  LegacyListPagedResult,
  ListPagedResult,
  PagedRequest,
  PayInstructionPayload,
} from '@xemplo/gp-types';
import { extractFilenameFromContentDisposition } from '@xemplo/gp-utils';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { AmendmentAPI } from './amendment-query.constants';
import { Id } from './amendment-query.types';

export const useGetAmendmentChangeLogQuery = (props: { id?: Id }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { id } = props;
  const url = apiUrlBuilder(AmendmentAPI.changeLogById(id), applicationApiUrl);

  return useQuery<ListPagedResult<AmendmentChangeLog>>({
    queryKey: amendmentQueryKeys.changeLog(String(id ?? ''), url),
    queryFn: ({ signal }) =>
      client
        .get<ListPagedResult<AmendmentChangeLog>>(url, { signal })
        .then((resp) => resp.data),
    enabled: !!applicationApiUrl && !!id,
  });
};

export const useGetChangeTasksQuery = (props: { requestParams?: PagedRequest }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams } = props;
  const url = apiUrlBuilder(AmendmentAPI.changeTasks, applicationApiUrl, requestParams);

  return useQuery<ListPagedResult<ChangeTask>>({
    queryKey: amendmentQueryKeys.changeTasks(url),
    queryFn: () => client.get<ListPagedResult<ChangeTask>>(url).then((resp) => resp.data),
    enabled: !!applicationApiUrl,
  });
};

export const useGetChangeTaskSubTypeQuery = (props: { requestParams?: PagedRequest }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams } = props;
  const url = apiUrlBuilder(
    AmendmentAPI.changeTaskSubTypes,
    applicationApiUrl,
    requestParams,
  );

  return useQuery({
    queryKey: amendmentQueryKeys.changeTaskSubTypes(url),
    queryFn: () =>
      client.get<ListPagedResult<ChangeSubTask>>(url).then((resp) => resp.data),
    enabled:
      !!applicationApiUrl &&
      !!requestParams?.q?.changeTaskValueId &&
      !!requestParams?.q?.businessUnitId,
    select: (data) => data?.result?.rows ?? [],
  });
};

export const useGetAmendmentFilesQuery = (props: { id?: Id }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { id } = props;
  const url = apiUrlBuilder(AmendmentAPI.filesByAmendmnetId(id), applicationApiUrl);

  return useQuery<LegacyListPagedResult<AmendmentFile>>({
    queryKey: amendmentQueryKeys.files(String(id ?? ''), url),
    queryFn: ({ signal }) =>
      client
        .get<LegacyListPagedResult<AmendmentFile>>(url, { signal })
        .then((resp) => resp.data),
    enabled: !!applicationApiUrl && !!id,
  });
};

export const useGetAmendmentFileDownloadQuery = (props: { id?: Id }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { id } = props;
  const url = apiUrlBuilder(AmendmentAPI.downloadFileByFileId(id), applicationApiUrl);

  return useQuery<Blob>({
    queryKey: amendmentQueryKeys.fileDownload(String(id ?? ''), url),
    queryFn: ({ signal }) =>
      client.get<Blob>(url, { signal, responseType: 'blob' }).then((resp) => {
        const contentType = resp.headers['content-type'];
        const blob = new Blob([resp.data], {
          type: contentType ? contentType.toString() : undefined,
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.setAttribute('href', url);
        const filename = extractFilenameFromContentDisposition(
          resp.headers['content-disposition'],
        );
        link.setAttribute('download', filename);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        return blob;
      }),
    enabled: !!applicationApiUrl && !!id,
  });
};

/**
 * Hook for creating amendments
 * @returns useMutation hook (Tanstack query) for submitting the create amendment post request
 */
export const useCreateAmendment = () => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();
  const url = apiUrlBuilder(AmendmentAPI.bulkCreateAmendment, applicationApiUrl);
  return useMutation({
    mutationFn: async (payload: AmendmentCreate) => {
      await client.post(url, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_AMENDMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: amendmentQueryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_WORKERS],
      });
    },
  });
};

/**
 * Hook for deleting individial payrun amendments by Id
 * @param props useDeletePayrunAmendmentProps
 * @returns useMutation hook (Tanstack query) for submitting the delete request to the payrun amendment action endpoint
 */
export const useDeletePayrunAmendment = ({
  payrunId,
  amendmentId,
}: {
  payrunId: string;
  amendmentId: string;
}) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();

  const url = apiUrlBuilder(
    AmendmentAPI.deletePayrunAmendmentById(payrunId, amendmentId),
    applicationApiUrl,
  );

  return useMutation({
    mutationFn: async () => {
      await client.delete(url);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_AMENDMENTS],
      });
      queryClient.invalidateQueries({ queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_WORKERS] });
      queryClient.invalidateQueries({ queryKey: amendmentQueryKeys.lists() });
    },
  });
};

/**
 * Hook for deleting individial amendments by Id
 * @returns useMutation hook (Tanstack query) for submitting the delete request to the amendment action endpoint
 */
export const useDeleteAmendment = ({ amendmentId }: { amendmentId: string }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();

  const url = apiUrlBuilder(AmendmentAPI.amendmentById(amendmentId), applicationApiUrl);

  return useMutation({
    mutationFn: async () => {
      await client.delete(url);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_AMENDMENTS],
      });
      queryClient.invalidateQueries({ queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_WORKERS] });
      queryClient.invalidateQueries({ queryKey: amendmentQueryKeys.lists() });
      queryClient.cancelQueries({
        queryKey: amendmentQueryKeys.detailsById(String(amendmentId)),
      });
    },
  });
};

export const useResubmitPayrunAmendment = ({
  payrunId,
  amendmentId,
}: {
  payrunId: Id;
  amendmentId: Id;
}) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();

  const url = apiUrlBuilder(
    AmendmentAPI.resubmitPayrunAmendmentById(payrunId, amendmentId),
    applicationApiUrl,
  );

  return useMutation({
    mutationFn: async (payload: { description: string }) => {
      await client.post(url, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_AMENDMENTS],
      });
      queryClient.invalidateQueries({ queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_WORKERS] });
      queryClient.invalidateQueries({ queryKey: amendmentQueryKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: amendmentQueryKeys.detailsById(String(amendmentId)),
      });
    },
  });
};

/**
 * Seperate Hook for creating pay instruction amendments
 * @returns useMutation hook (Tanstack query) for submitting the create amendment post request
 */
export function useCreatePayInstruction() {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();
  const url = apiUrlBuilder(AmendmentAPI.createPayInstruction, applicationApiUrl);

  return useMutation({
    //payload should conform to the PayInstructionPayload interface defined in gp-types
    mutationFn: async (payload: PayInstructionPayload) => {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value) {
          formData.append(key, value);
        }
      });
      await client.post(url, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_AMENDMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: amendmentQueryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_WORKERS],
      });
    },
  });
}
